import { getErrorMessage } from "@helper/getErrorMessage";
import { reactionCount } from "@helper/utils";
import type { HandleReactionClick, ReactionProp } from "@typings/Reactions";
import { type ReactElement, useCallback, useState } from "react";

import { getReactionGif } from "./helper/getReactionGif";

type ArticleReactionOptionsProps = {
  reactions: ReactionProp[];
  handleReactionClick: HandleReactionClick;
  reactionsUser: string;
  articleId: string;
  variant?: "top" | "bottom";
};

const TOP_CARD_SHADOW = "0px 8px 16px 0px rgba(0, 0, 0, 0.16)";
const BASE_COUNT_CLASS =
  "text-center font-semimedium text-sm leading-[150%] text-grey-500";

export const ArticleReactionOptions = ({
  reactions,
  handleReactionClick,
  reactionsUser,
  articleId,
  variant = "top",
}: ArticleReactionOptionsProps): ReactElement | null => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClick = useCallback(
    async (reactionId: string, currentVariant?: "top" | "bottom") => {
      setIsSubmitting(true);
      try {
        await handleReactionClick(reactionId, currentVariant);
      } catch (err) {
        console.log("Reactions not working", getErrorMessage(err));
      } finally {
        setIsSubmitting(false);
      }
    },
    [handleReactionClick],
  );

  if (reactions.length === 0) {
    return null;
  }

  const isBottomVariant = variant === "bottom";

  const cardStyle = !isBottomVariant
    ? { boxShadow: TOP_CARD_SHADOW }
    : undefined;

  const wrapperClass = isBottomVariant
    ? "flex flex-col lg:flex-row gap-3.5 py-4 px-8 justify-center lg:justify-between items-center bg-red-700"
    : "flex w-max py-3 px-8 justify-center items-center rounded-3xl bg-red-750";

  const listWrapperClass = `flex items-start ${isBottomVariant ? "gap-8" : "gap-7"}`;

  return (
    <div className={wrapperClass} style={cardStyle}>
      {isBottomVariant && (
        <div
          data-testid="reaction-prompt"
          className="text-center lg:text-left text-grey-500 text-sm font-bold font-opensans leading-[150%]"
        >
          Did you enjoy this story? Let us know.
        </div>
      )}
      <ul className={listWrapperClass}>
        {reactions.map((reaction) => (
          <li key={`reactions-li-${variant}-${reaction.id}-${articleId}`}>
            <button
              disabled={isSubmitting}
              onClick={() => {
                void handleClick(reaction.id, variant);
              }}
              className="flex flex-col justify-center items-center gap-1"
              aria-label={reaction.text}
            >
              <img
                src={getReactionGif(reaction.id)}
                alt={reaction.text}
                width={24}
              />
              <div
                className={`${BASE_COUNT_CLASS} ${
                  reactionsUser === reaction.id && isBottomVariant
                    ? "!font-bold"
                    : ""
                }`}
              >
                {reactionCount(reaction.count)}
              </div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};
